<template>
  <v-container class="client-admin-risk-category-management">
    <div class="client-admin-risk-category-management-container">
      <div class="risk-category-management-title">Risk Category Management</div>
      <div class="risk-category-management-description">
        {{
          `Here you can manage your Risk Categories that you want to remove as options in your Excursions.`
        }}
      </div>
      <div class="risk-category-management-description">
        Select and tick the check box next to the Risk you wish to remove from Excursions.
      </div>
      <div class="risk-category-management-selector-container">
        <div class="risk-category-management-selector">Risk Category Selector</div>
        <div>
          <cs-button
            id="clientAdminRiskCategorySaveBtn"
            primary
            label="Save"
            @click="riskCategoryPresaveDialogDisplayed = true"
          ></cs-button>
          <cs-button
            id="clientAdminRiskCategoryCancelBtn"
            label="Cancel"
            @click="resetRiskCategorySelection"
          ></cs-button>
        </div>
      </div>
      <v-data-table
        :headers="groupHeaders"
        :items="riskCategoryList"
        class="elevation-2 excursion-table"
        :loading="riskCategoryLoading"
        :items-per-page-options="footerProps['items-per-page-options']"
        item-value="sid"
      >
        <template #no-data>
          <span class="table-no-data-text">No risk category was found</span>
        </template>
        <template #headers="{ columns, isSorted, getSortIcon, toggleSort }">
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :style="{
                width: column.width + 'px',
                'min-width': column.width + 'px',
                padding: 8 + 'px'
              }"
            >
              <span class="mr-2 cursor-pointer table-header" @click="() => toggleSort(column)">
                {{ column.title }}
                <template v-if="isSorted(column)">
                  <v-icon :icon="getSortIcon(column)"></v-icon>
                </template>
              </span>
            </th>
          </tr>
        </template>
        <template #item="{ item }">
          <tr>
            <td class="risk-catetory-cell">
              <v-checkbox-btn
                v-model="item.riskCategoryBlacklistFlag"
                color="primary"
                hide-details
                :class="item.riskCategoryBlacklistFlag ? 'risk-category-checkbox' : ''"
                :ripple="false"
                @update:model-value="clickCheckbox(item)"
              ></v-checkbox-btn>
            </td>
            <td class="text-left">{{ item.riskCategory }}</td>
            <td class="text-left">{{ item.riskType }}</td>
            <td class="text-left">{{ item.riskClass }}</td>
            <td class="text-left">{{ item.riskName }}</td>
          </tr>
        </template>
      </v-data-table>
    </div>
    <cs-form-dialog
      id="dlg-risk-category-management"
      v-model="riskCategoryPresaveDialogDisplayed"
      heading="Risk Category Management"
      body-id="risk-category-pre-save-dialog"
      :primary-action="{
        label: 'Save',
        eventName: 'primary-click',
        disabled: clientRiskCategoryModalSaveActionInProgress
      }"
      :secondary-action1="{ label: 'Cancel', eventName: 'secondary1-click' }"
      @primary-click="saveClientRiskCategoryModal"
      @secondary1-click="riskCategoryPresaveDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <v-spacer></v-spacer>
        <v-card-text>Do you want to save the changes?</v-card-text>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-risk-category-management-generic"
      v-model="clientAdminRiskCategoryManagementGenericDialogDisplayed"
      heading="Risk Category Management"
      body-id="client-admin-risk-category-management-modal--generic-dialog"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      @primary-click="clientAdminRiskCategoryManagementGenericDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <v-spacer></v-spacer>
        <div v-safe-html="riskCategoryGenericDialogMessage"></div>
      </template>
    </cs-form-dialog>
  </v-container>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { mapState } from 'vuex';
import { populateErrorMessage } from '../lib/errorMessages';

export default {
  name: 'ClientAdminRiskCategoryManagement',

  extends: CSBase,

  data() {
    return {
      riskCategoryPresaveDialogDisplayed: false,
      groupHeaders: [
        {
          title: 'Select to Remove',
          key: 'riskCategoryBlacklistFlag',
          width: 100
        },
        {
          title: 'Risk Category',
          key: 'riskCategory',
          width: 200
        },
        {
          title: 'Risk Type',
          key: 'riskType',
          width: 200
        },
        {
          title: 'Risk Class',
          key: 'riskClass',
          width: 200
        },
        {
          title: 'Risk',
          key: 'riskName',
          width: 200
        }
      ],
      riskCategoryLoading: false,
      footerProps: {
        'items-per-page-options': [5, 10, 25]
      },
      riskCategoryList: [],
      originalRiskCategoryList: [],
      clientAdminRiskCategoryManagementGenericDialogDisplayed: false,
      riskCategoryGenericDialogMessage: '',
      riskCategoryGenericDialogSuccessMessage: 'Risk category blacklist has been updated.',
      clientRiskCategoryModalSaveActionInProgress: false
    };
  },
  computed: {
    ...mapState({
      clientSiteSettingsButtonDiplayed: (state) => state.common.clientSiteSettingsButtonDiplayed,
      riskCategoryManagementMenuItemDisplayed: (state) =>
        state.common.riskCategoryManagementMenuItemDisplayed,
      isAuthenticated: (state) => state.authorisation.isAuthenticated
    })
  },
  async mounted() {
    await this.initView();
  },
  methods: {
    async initView() {
      const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
      const isVendorAuthScheme =
        !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
      if (!isVendorAuthScheme) {
        await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
          query: this.$route.query,
          axiosInstance
        });
      }
      await this.$store.dispatch('common/initClientConfig', {
        queryParam: isVendorAuthScheme ? this.$route.query : {},
        eventPluginCommonAdapter,
        stbApiAdapter
      });
      if (!this.clientSiteSettingsButtonDiplayed || !this.riskCategoryManagementMenuItemDisplayed) {
        this.$router.push('/excursions');
      }
      if (this.isAuthenticated) {
        await this.getRiskCategories();
      }
    },
    resetRiskCategorySelection() {
      this.riskCategoryList = JSON.parse(JSON.stringify(this.originalRiskCategoryList));
    },
    async saveClientRiskCategoryModal() {
      const newCategoryRiskBlacklist = this.riskCategoryList
        .filter((item) => item.riskCategoryBlacklistFlag)
        .map((item) => ({ [item.sid]: true }))
        .reduce((result, current) => ({ ...result, [Object.keys(current)[0]]: true }), {});

      const argHash = {
        payload: newCategoryRiskBlacklist,
        store: this.$store
      };
      this.clientRiskCategoryModalSaveActionInProgress = true;
      const cbResponse = await this.$app.stbApiAdapter.putClientRiskCategoryBlacklist(argHash);
      this.clientRiskCategoryModalSaveActionInProgress = false;
      this.riskCategoryPresaveDialogDisplayed = false;
      if (!cbResponse.error) {
        this.riskCategoryGenericDialogMessage = this.riskCategoryGenericDialogSuccessMessage;
        this.clientAdminRiskCategoryManagementGenericDialogDisplayed = true;
        this.originalRiskCategoryList = JSON.parse(JSON.stringify(this.riskCategoryList));
      } else {
        let dialogMessage = 'Can not update risk category blacklist.';
        if (cbResponse.refinedMessage) {
          dialogMessage = `${dialogMessage} ${cbResponse.refinedMessage}.`;
        }

        this.riskCategoryGenericDialogMessage = populateErrorMessage({
          message: dialogMessage,
          store: this.$store
        });
        this.clientAdminRiskCategoryManagementGenericDialogDisplayed = true;
      }
    },
    async getRiskCategories() {
      const { query } = this.$route;
      this.riskCategoryLoading = true;
      const cbResponse = await this.$app.stbApiAdapter.getClientRiskCategories({
        queryParam: query,
        store: this.$store
      });
      this.riskCategoryLoading = false;
      if (!cbResponse.error) {
        this.riskCategoryList = cbResponse.data;
        this.originalRiskCategoryList = JSON.parse(JSON.stringify(cbResponse.data));
      }
    },
    clickCheckbox(item) {
      const riskPath = String(item.path).split('|');
      if (riskPath.length === 1) {
        this.riskCategoryList.forEach((risk, riskIndex) => {
          if (risk.riskCategory === item.path) {
            this.riskCategoryList[riskIndex].riskCategoryBlacklistFlag =
              item.riskCategoryBlacklistFlag;
          }
        });
      }
      if (riskPath.length === 2) {
        this.riskCategoryList.forEach((risk, riskIndex) => {
          if (risk.riskCategory === item.riskCategory && risk.riskType === item.riskType) {
            this.riskCategoryList[riskIndex].riskCategoryBlacklistFlag =
              item.riskCategoryBlacklistFlag;
          }
        });
      }
      if (riskPath.length === 3) {
        this.riskCategoryList.forEach((risk, riskIndex) => {
          if (
            risk.riskCategory === item.riskCategory &&
            risk.riskType === item.riskType &&
            risk.riskClass === item.riskClass
          ) {
            this.riskCategoryList[riskIndex].riskCategoryBlacklistFlag =
              item.riskCategoryBlacklistFlag;
          }
        });
      }
    }
  }
};
</script>
<style scoped>
.client-admin-risk-category-management {
  width: 100%;
  height: 100%;
  max-width: unset;
  margin: 0;
  padding: 0;
}
.settings-content {
  text-align: left;
  height: 100vh;
}
.client-admin-risk-category-management-container {
  padding-left: 50px;
  padding-right: 50px;
  padding-top: 30px;
  height: 100%;
}
.risk-category-management-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  display: flex;
}
.risk-category-management-description {
  display: flex;
  font-weight: 500;
  color: gray;
}
.risk-category-management-selector-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  margin-bottom: 20px;
}
.risk-category-management-selector {
  font-weight: bold;
  color: gray;
}

.risk-category-checkbox {
  padding-top: 1px;
}

.risk-category-checkbox > i {
  color: rgb(var(--v-theme-primary)) !important;
}

.table-header {
  font-weight: 700 !important;
  font-size: 12px !important;
  line-height: 18px !important;
  white-space: nowrap;
}
</style>
